import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Collapse, Paper, Stack, Typography } from '@mui/material';

type AppUpdateScope =
  | 'global'
  | 'pricing'
  | 'industrial';

type AppVersionPayload = {
  version?: string;
  built_at?: string;
  affected_scopes?: string[];
};

const VALID_APP_UPDATE_SCOPES =
  new Set<AppUpdateScope>([
    'global',
    'pricing',
    'industrial',
  ]);

const INDUSTRIAL_PATH_PREFIXES = [
  '/industrial-cost',
  '/factory',
  '/industrial-risk',
];

const VERSION_URL = '/version.json';
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

async function fetchAppVersion(signal?: AbortSignal): Promise<AppVersionPayload | null> {
  const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
    method: 'GET',
    cache: 'no-store',
    signal,
  });

  if (!response.ok) {
    throw new Error(`No se pudo consultar version.json (${response.status})`);
  }

  const data = (await response.json()) as AppVersionPayload;
  return data;
}

function normalizeAffectedScopes(
  rawScopes: string[] | undefined,
): AppUpdateScope[] {
  if (
    !Array.isArray(rawScopes) ||
    rawScopes.length === 0
  ) {
    return ['global'];
  }

  const scopes = rawScopes
    .map((scope) => scope.trim())
    .filter(Boolean);

  if (
    scopes.length === 0 ||
    scopes.some(
      (scope) =>
        !VALID_APP_UPDATE_SCOPES.has(
          scope as AppUpdateScope,
        ),
    )
  ) {
    return ['global'];
  }

  if (scopes.includes('global')) {
    return ['global'];
  }

  return Array.from(
    new Set(scopes as AppUpdateScope[]),
  );
}

function isPathWithin(
  pathname: string,
  basePath: string,
) {
  return (
    pathname === basePath ||
    pathname.startsWith(`${basePath}/`)
  );
}

function getCurrentAppScope(
  pathname: string,
): Exclude<AppUpdateScope, 'global'> {
  return INDUSTRIAL_PATH_PREFIXES.some(
    (basePath) =>
      isPathWithin(pathname, basePath),
  )
    ? 'industrial'
    : 'pricing';
}

function doesUpdateAffectCurrentPath(
  affectedScopes: AppUpdateScope[],
  pathname: string,
) {
  return (
    affectedScopes.includes('global') ||
    affectedScopes.includes(
      getCurrentAppScope(pathname),
    )
  );
}

function formatShortVersion(version: string | null): string | null {
  if (!version) {
    return null;
  }

  const trimmed = version.trim();
  if (!trimmed) {
    return null;
  }

  return trimmed.slice(0, 7);
}

export default function AppVersionUpdateNotifier() {
  const { pathname } = useLocation();

  const [hasUpdateAvailable, setHasUpdateAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [
    latestAffectedScopes,
    setLatestAffectedScopes,
  ] = useState<AppUpdateScope[]>(['global']);

  const currentVersionRef = useRef<string | null>(null);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    let disposed = false;
    const controller = new AbortController();

    const checkVersion = async () => {
      try {
        const payload = await fetchAppVersion(controller.signal);
        const nextVersion = payload?.version?.trim() || null;
        const nextAffectedScopes =
          normalizeAffectedScopes(
            payload?.affected_scopes,
          );

        if (!nextVersion || disposed) {
          return;
        }

        if (!hasInitializedRef.current) {
          currentVersionRef.current = nextVersion;
          hasInitializedRef.current = true;
          return;
        }

        if (currentVersionRef.current && nextVersion !== currentVersionRef.current) {
          setLatestVersion(nextVersion);
          setLatestAffectedScopes(
            nextAffectedScopes,
          );
          setHasUpdateAvailable(true);
        }
      } catch {
        // No bloqueamos la app si falla la comprobación.
      }
    };

    void checkVersion();

    const intervalId = window.setInterval(() => {
      void checkVersion();
    }, CHECK_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkVersion();
      }
    };

    const handleFocus = () => {
      void checkVersion();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      disposed = true;
      controller.abort();
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  if (
    !hasUpdateAvailable ||
    !doesUpdateAffectCurrentPath(
      latestAffectedScopes,
      pathname,
    )
  ) {
    return null;
  }

  const shortVersion = formatShortVersion(latestVersion);

  return (
    <Collapse in>
      <Box
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 2000,
          width: 'min(400px, calc(100vw - 32px))',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid rgba(34,211,238,0.22)',
            bgcolor: 'rgba(2,6,23,0.96)',
            boxShadow: '0 14px 34px rgba(2,6,23,0.42)',
            overflow: 'hidden',
          }}
        >
          <Stack spacing={0} sx={{ p: 2 }}>
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <InfoOutlinedIcon
                sx={{
                  color: '#22d3ee',
                  mt: '2px',
                  fontSize: 20,
                  flexShrink: 0,
                }}
              />

              <Stack spacing={1.25} sx={{ minWidth: 0, flex: 1 }}>
                <Box>
                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 900,
                      fontSize: '1rem',
                      lineHeight: 1.2,
                    }}
                  >
                    Nueva versión disponible
                  </Typography>

                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.9rem',
                      mt: 0.5,
                      lineHeight: 1.45,
                    }}
                  >
                    Se ha desplegado una actualización. Puedes recargar ahora para usar
                    la versión más reciente.
                  </Typography>

                  {shortVersion && (
                    <Typography
                      sx={{
                        color: '#67e8f9',
                        fontSize: '0.78rem',
                        mt: 0.9,
                        fontWeight: 700,
                        letterSpacing: '0.02em',
                      }}
                    >
                      Versión detectada: {shortVersion}
                    </Typography>
                  )}
                </Box>

                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant="text"
                    onClick={() => setHasUpdateAvailable(false)}
                    sx={{
                      color: '#cbd5e1',
                      fontWeight: 700,
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: 'rgba(148,163,184,0.08)',
                      },
                    }}
                  >
                    Más tarde
                  </Button>

                  <Button
                    variant="contained"
                    onClick={() => {
                      window.location.reload();
                    }}
                    sx={{
                      bgcolor: '#22d3ee',
                      color: '#082f49',
                      fontWeight: 800,
                      textTransform: 'none',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: '#67e8f9',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    Actualizar ahora
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    </Collapse>
  );
}